import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { mockDocuments, mockFollowUps, mockLeads, mockLoans } from '../../data/mockData';

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(
    amount || 0
  );

const statusBadge = (status) => {
  const map = {
    Pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300',
    Scheduled: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
    Overdue: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
    Verified: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300',
    Rejected: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
    Approved: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
  };
  return map[status] || 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300';
};

export const PendingApprovals = () => {
  const pendingApprovals = useMemo(() => mockLoans.filter((l) => l.status === 'Approved'), []);

  return (
    <div className="space-y-6" data-testid="admin-alerts-pending-approvals">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold font-['Plus_Jakarta_Sans']">Pending Approvals</h1>
        <p className="text-muted-foreground mt-1">Loans awaiting final approval / payout actions</p>
      </div>

      <Card className="border border-border">
        <CardHeader>
          <CardTitle>Approved Loans (Action Required)</CardTitle>
          <CardDescription>Review before disbursal and commission processing</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Loan ID</TableHead>
                <TableHead>Applicant</TableHead>
                <TableHead>Bank</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pendingApprovals.map((loan) => (
                <TableRow key={loan.id}>
                  <TableCell className="font-mono text-sm">{loan.id}</TableCell>
                  <TableCell className="font-medium">{loan.applicantName}</TableCell>
                  <TableCell>{loan.bank}</TableCell>
                  <TableCell className="font-medium">{formatCurrency(loan.amount)}</TableCell>
                  <TableCell>
                    <Badge className={statusBadge(loan.status)}>{loan.status}</Badge>
                  </TableCell>
                  <TableCell className="text-right space-x-2">
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700">
                      Approve
                    </Button>
                    <Button size="sm" variant="outline">
                      Reject
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {pendingApprovals.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10 text-muted-foreground">
                    No pending approvals right now.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export const OverdueFiles = () => {
  const overdueFollowUps = useMemo(() => mockFollowUps.filter((f) => f.status === 'Overdue'), []);
  const rejectedDocs = useMemo(() => mockDocuments.filter((d) => d.status === 'Rejected'), []);

  return (
    <div className="space-y-6" data-testid="admin-alerts-overdue-files">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold font-['Plus_Jakarta_Sans']">Overdue Files</h1>
        <p className="text-muted-foreground mt-1">Follow-ups and documents needing urgent attention</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border border-border">
          <CardHeader>
            <CardTitle>Overdue Follow-Ups</CardTitle>
            <CardDescription>Tasks past scheduled date/time</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Lead</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Scheduled</TableHead>
                  <TableHead>Assigned To</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {overdueFollowUps.map((f) => (
                  <TableRow key={f.id}>
                    <TableCell className="font-medium">
                      <div className="flex flex-col">
                        <span>{f.leadName}</span>
                        <span className="text-xs text-muted-foreground font-mono">{f.leadId}</span>
                      </div>
                    </TableCell>
                    <TableCell>{f.type}</TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span>{f.scheduledDate}</span>
                        <span className="text-xs text-muted-foreground">{f.scheduledTime}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={statusBadge(f.status)}>{f.assignedTo}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
                {overdueFollowUps.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-10 text-muted-foreground">
                      No overdue follow-ups.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card className="border border-border">
          <CardHeader>
            <CardTitle>Rejected Documents</CardTitle>
            <CardDescription>Documents rejected during verification</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Lead</TableHead>
                  <TableHead>Doc Type</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rejectedDocs.map((d) => (
                  <TableRow key={d.id}>
                    <TableCell className="font-medium">
                      <div className="flex flex-col">
                        <span>{d.leadName}</span>
                        <span className="text-xs text-muted-foreground font-mono">{d.leadId}</span>
                      </div>
                    </TableCell>
                    <TableCell>{d.docType}</TableCell>
                    <TableCell>
                      <Badge className={statusBadge(d.status)}>{d.status}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
                {rejectedDocs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center py-10 text-muted-foreground">
                      No rejected documents.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export const HighValueAlerts = () => {
  const highValueLeads = useMemo(() => mockLeads.filter((l) => (l.amount || 0) >= 4000000 || l.priority === 'High'), []);

  return (
    <div className="space-y-6" data-testid="admin-alerts-high-value">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold font-['Plus_Jakarta_Sans']">High Value Alerts</h1>
        <p className="text-muted-foreground mt-1">High ticket leads and priority opportunities</p>
      </div>

      <Card className="border border-border">
        <CardHeader>
          <CardTitle>Priority Leads</CardTitle>
          <CardDescription>Leads with high amount or high priority</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Lead</TableHead>
                <TableHead>Loan Type</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Priority</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {highValueLeads.map((lead) => (
                <TableRow key={lead.id}>
                  <TableCell className="font-medium">
                    <div className="flex flex-col">
                      <span>{lead.name}</span>
                      <span className="text-xs text-muted-foreground font-mono">{lead.id}</span>
                    </div>
                  </TableCell>
                  <TableCell>{lead.loanType}</TableCell>
                  <TableCell className="font-medium">{formatCurrency(lead.amount)}</TableCell>
                  <TableCell>
                    <Badge className={statusBadge(lead.status)}>{lead.status}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={statusBadge(lead.priority)}>
                      {lead.priority}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline">
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {highValueLeads.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10 text-muted-foreground">
                    No high value alerts.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default PendingApprovals;

